import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, CalendarClock } from 'lucide-react';
import { Reveal, SectionTitle, Counter, Btn, Icon, Kicker } from '../components/common';
import { garden, heroImages, img, pillars, stats, gardenCollections, missionText } from '../mock/mock';
import { api } from '../lib/api';

const Home = () => {
  const [c, setC] = useState({});
  useEffect(() => { api.get('/content').then((r) => setC(r.data || {})).catch(() => setC({})); }, []);
  return (
    <div>
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImages[0]} alt="Botanical garden" className="w-full h-full object-cover kenburns" />
          <div className="absolute inset-0 bg-gradient-to-b from-forest-dark/70 via-forest-dark/40 to-forest-dark/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full">
          <Reveal>
            <Kicker light>{garden.opening}</Kicker>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-display text-[2.6rem] sm:text-6xl lg:text-7xl text-white leading-[1.02] mt-5 max-w-4xl">
              {c.hero_title_lead || 'A new botanical garden is growing in'} <span className="text-gold-light italic">{c.hero_title_accent || 'South Fulton'}</span>{c.hero_title_tail || ', Georgia'}
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-6 text-cream/85 max-w-xl text-lg leading-relaxed">
              {c.hero_sub || '40 acres of gardens, woodland and wonder on the historic Camp Truitt property — cultivated to connect our community with the beauty of the natural world.'}
            </p>
          </Reveal>
          <Reveal delay={360}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Btn to="/vision">Explore the Vision <ArrowRight className="w-4 h-4" /></Btn>
              <Btn to="/give" variant="outline">Support the Garden</Btn>
            </div>
          </Reveal>
          <Reveal delay={480}>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-cream/80 text-sm">
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gold-light" /> {garden.location}</span>
              <span className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-gold-light" /> {garden.opening}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MISSION INTRO */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img src={img.formalGarden} alt="Formal garden" className="w-full h-[520px] object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-8 -right-6 w-56 rounded-xl overflow-hidden shadow-2xl border-4 border-cream">
              <img src={img.hydrangea} alt="Hydrangea" className="w-full h-44 object-cover" />
            </div>
          </Reveal>
          <Reveal delay={150}>
            <SectionTitle kicker="Our Mission" title={c.mission_heading || 'Cultivating, conserving, displaying & celebrating plants'} />
            <p className="mt-6 text-ink/70 leading-relaxed">{c.mission_text || missionText}</p>
            <div className="mt-8"><Btn to="/vision" variant="outlineDark">Discover our Vision <ArrowRight className="w-4 h-4" /></Btn></div>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 bg-forest text-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center light kicker="What We Do" title="Rooted in four enduring commitments" /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <div className="h-full p-8 rounded-2xl bg-forest-dark/60 border border-forest-light/30 hover:border-gold/60 hover:-translate-y-1.5 transition-all duration-300 group">
                  <div className="w-14 h-14 rounded-xl bg-gold/15 flex items-center justify-center mb-6 group-hover:bg-gold/25 transition-colors">
                    <Icon name={p.icon} className="w-7 h-7 text-gold-light" />
                  </div>
                  <h3 className="font-display text-2xl text-white mb-3">{p.title}</h3>
                  <p className="text-cream/70 text-sm leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 bg-sand">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 100} className="text-center">
              <div className="font-display text-5xl md:text-6xl text-forest font-500">
                <Counter value={s.value} suffix={s.suffix} prefix={s.prefix || ''} />
              </div>
              <p className="mt-3 text-ink/60 text-sm max-w-[180px] mx-auto">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <Reveal><SectionTitle kicker="The Gardens" title="Landscapes to wander & wonder" sub="A journey through fifteen distinct collections, each designed to delight across the seasons." /></Reveal>
            <Reveal delay={150}><Btn to="/vision" variant="outlineDark">View all gardens <ArrowRight className="w-4 h-4" /></Btn></Reveal>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {gardenCollections.map((c, i) => (
              <Reveal key={c.name} delay={(i % 3) * 100}>
                <div className="group relative rounded-2xl overflow-hidden shadow-lg h-80 cursor-pointer">
                  <img src={img[c.img]} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/90 via-forest-dark/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-display text-2xl text-white">{c.name}</h3>
                    <p className="text-cream/0 group-hover:text-cream/85 text-sm mt-2 max-h-0 group-hover:max-h-24 overflow-hidden transition-all duration-500">{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={img.lushPath} alt="Garden path" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-forest-dark/80" />
        </div>
        <div className="relative max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <Kicker light>Be Part of the Story</Kicker>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-tight mt-5">{c.cta_title || 'Help us grow a garden for generations'}</h2>
            <p className="mt-5 text-cream/85 text-lg">{c.cta_text || 'Founding gifts today plant the gardens our children will one day explore. Join us in cultivating something extraordinary.'}</p>
            <div className="mt-9 flex flex-wrap gap-4 justify-center">
              <Btn to="/give">Make a Gift <ArrowRight className="w-4 h-4" /></Btn>
              <Btn to="/programs" variant="outline">Become a Member</Btn>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
